<template>
  <div class="bg-white rounded-lg border-2 border-gray-900 p-6">
    <!-- Header -->
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-xs text-gray-500 uppercase tracking-wider font-medium">
        Symptoms
      </h3>
      <button
        @click="shareReport"
        class="text-xs text-gray-400 hover:text-gray-600 flex items-center">
        <svg class="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
        </svg>
        Share
      </button>
    </div>

    <!-- Main Stats -->
    <div class="flex items-center justify-between mb-4">
      <div>
        <p class="text-3xl font-bold">
          {{ totalSymptoms }}
        </p>
        <p class="text-xs text-gray-500">
          symptoms logged
        </p>
      </div>
      <div class="text-right">
        <div class="flex items-center justify-end text-green-600">
          <svg class="w-5 h-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span class="text-2xl font-bold">{{ symptomFreeStreak }}</span>
        </div>
        <p class="text-xs text-gray-500">
          symptom-free days
        </p>
      </div>
    </div>

    <!-- Recent Timeline -->
    <div v-if="recentTimeline.length > 0" class="mb-4">
      <p class="text-xs text-gray-500 mb-2">
        Recent (7 Days)
      </p>
      <div class="space-y-2">
        <div
          v-for="item in recentTimeline.slice(0, 3)"
          :key="item.id"
          class="flex items-center justify-between p-2 bg-gray-50 rounded">
          <div class="flex items-center">
            <span
              :class="[
                'w-2 h-2 rounded-full mr-2',
                getSeverityColor(item.severity)
              ]" />
            <span class="text-sm">{{ formatSymptomType(item.symptom_type) }}</span>
          </div>
          <span class="text-xs text-gray-400">{{ formatDate(item.date) }}</span>
        </div>
      </div>
    </div>

    <!-- Top Symptoms This Month -->
    <div v-if="topSymptoms.length > 0" class="mb-4">
      <p class="text-xs text-gray-500 mb-2">
        Top This Month
      </p>
      <div class="flex flex-wrap gap-2">
        <span
          v-for="symptom in topSymptoms"
          :key="symptom.symptom_type"
          class="inline-flex items-center px-2 py-1 bg-gray-100 rounded text-xs">
          {{ formatSymptomType(symptom.symptom_type) }}
          <span class="ml-1 text-gray-400">{{ symptom.count }}</span>
        </span>
      </div>
    </div>

    <!-- Category Frequency -->
    <div v-if="hasCategories" class="pt-4 border-t border-gray-100">
      <p class="text-xs text-gray-500 mb-2">
        By Category
      </p>
      <div class="space-y-2">
        <div
          v-for="(count, category) in categoryData"
          :key="category"
          class="flex items-center">
          <span class="text-xs text-gray-600 w-20 capitalize">{{ category }}</span>
          <div class="flex-1 bg-gray-100 rounded-full h-1.5 mx-2">
            <div
              class="bg-gray-900 h-1.5 rounded-full"
              :style="{ width: `${getCategoryWidth(count)}%` }" />
          </div>
          <span class="text-xs text-gray-500 w-6 text-right">{{ count }}</span>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-if="totalSymptoms === 0" class="text-center py-4">
      <svg class="w-12 h-12 mx-auto text-gray-300 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <p class="text-sm text-gray-500">
        No symptoms logged
      </p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SymptomsCard',

  props: {
    data: {
      type: Object,
      default: () => ({})
    },
    streak: {
      type: Object,
      default: () => ({})
    }
  },

  emits: ['share'],

  computed: {
    totalSymptoms() {
      return this.data?.total_symptoms_in_period || 0
    },
    symptomFreeStreak() {
      return this.streak?.current_streak || 0
    },
    recentTimeline() {
      return this.data?.recent_timeline || []
    },
    topSymptoms() {
      return (this.data?.top_symptoms_this_month || []).slice(0, 5)
    },
    categoryData() {
      return this.data?.category_frequency_data || {}
    },
    hasCategories() {
      return Object.keys(this.categoryData).some((k) => this.categoryData[k] > 0)
    },
    maxCategoryCount() {
      const counts = Object.values(this.categoryData)
      return counts.length > 0 ? Math.max(...counts) : 1
    }
  },

  methods: {
    shareReport() {
      this.$emit('share')
    },

    formatSymptomType(type) {
      if (!type) return ''
      return type
        .replace(/HKCategoryTypeIdentifier/g, '')
        .replace(/([A-Z])/g, ' $1')
        .trim()
    },

    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      const today = new Date()
      const diffDays = Math.floor((today - date) / (1000 * 60 * 60 * 24))

      if (diffDays === 0) return 'Today'
      if (diffDays === 1) return 'Yesterday'
      if (diffDays < 7) return `${diffDays}d ago`
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    },

    getSeverityColor(severity) {
      const level = parseInt(severity, 10) || 0
      if (level >= 3) return 'bg-red-500'
      if (level >= 2) return 'bg-orange-500'
      if (level >= 1) return 'bg-yellow-500'
      return 'bg-gray-300'
    },

    getCategoryWidth(count) {
      if (this.maxCategoryCount === 0) return 0
      return (count / this.maxCategoryCount) * 100
    }
  }
}
</script>
